import {
  Badge,
  Box,
  Checkbox,
  Radio,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { NotInRoundBadge } from "../evaluation/NotInRoundBadge";
import { FiAward } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {
  AwardMethodDto,
  BidStatus,
  EvaluationModelDto,
  type EvalBidDto,
  type EvaluationDto,
  type EventValueDto,
} from "../../../autogen/bff-api";
import { MinimumRequirementOutcomeBadge } from "../minimum-requirements/MinimumRequirementOutcomeBadge";
import { formatMoney } from "../../../common/formatting/displaySum";

const formatPrice = (v?: EventValueDto | null): string =>
  v?.value != null ? `${formatMoney(v.value)} ${v.currency}` : "–";

export const isRejectedBid = (bid: EvalBidDto): boolean => bid.status === BidStatus.Rejected;

export const isUnrankedBid = (bid: EvalBidDto): boolean => !isRejectedBid(bid) && bid.rank <= 0;

export const hasAnyScore = (bid: EvalBidDto): boolean => bid.cells.some((cell) => cell.points != null);

export const ranksByPriceOnly = (evaluation: Pick<EvaluationDto, "model" | "awardMethod">): boolean =>
  evaluation.model === EvaluationModelDto.Price || evaluation.awardMethod === AwardMethodDto.LowestPrice;

type AwardBidsTableProps = {
  evaluation: EvaluationDto;
  selectedBidIds: string[];
} & (
  | { mode: "select"; onToggle: (bidId: string) => void; awardsOneSupplier?: boolean }
  | { mode: "confirmed"; onToggle?: never; awardsOneSupplier?: never }
);

const sortOrder = (bid: EvalBidDto): number => (isRejectedBid(bid) ? 2 : isUnrankedBid(bid) ? 1 : 0);

export const sortAwardBids = (bids: EvalBidDto[]) =>
  [...bids].sort((a, b) => sortOrder(a) - sortOrder(b) || a.rank - b.rank);

export const AwardBidsTable = ({
  evaluation,
  mode,
  selectedBidIds,
  onToggle,
  awardsOneSupplier = false,
}: AwardBidsTableProps) => {
  const { t } = useTranslation();
  const priceOnly = ranksByPriceOnly(evaluation);
  const byJury = evaluation.rankedByJury ?? false;
  const bids = sortAwardBids(evaluation.bids);

  const table = (
    <Table variant="simple" size={byJury ? "sm" : "md"}>
      <Thead>
        <Tr>
          {!byJury && (
            <Th w="1px" whiteSpace="nowrap">
              {mode === "select" ? t("Select") : t("Winner")}
            </Th>
          )}
          <Th w="1px" textAlign="center">
            {byJury ? t("Placing") : "#"}
          </Th>
          <Th w="100%">{byJury ? t("Participant") : t("Supplier")}</Th>
          {byJury ? (
            <Th w="1px" whiteSpace="nowrap">
              {mode === "select" ? t("Select") : t("Winner")}
            </Th>
          ) : (
            <Th w="1px" whiteSpace="nowrap" isNumeric>
              {priceOnly ? t("Bid sum") : t("Total score")}
            </Th>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {bids.map((bid) => {
          const isRejected = isRejectedBid(bid);
          const isUnranked = isUnrankedBid(bid);
          const isWinner = selectedBidIds.includes(bid.bidId);
          const rowToggles = mode === "select" && byJury && !isRejected;
          const selectCell = (
            <Td
              w="1px"
              onClick={rowToggles ? (e) => e.stopPropagation() : undefined}
              borderLeft={isRejected || isWinner ? "3px solid" : undefined}
              borderLeftColor={
                isRejected ? "red.300" : isWinner ? "green.400" : undefined
              }
            >
              {mode === "select" && awardsOneSupplier ? (
                <Radio
                  name="award-winner"
                  isChecked={isWinner}
                  onChange={() => onToggle(bid.bidId)}
                  colorScheme="teal"
                  isDisabled={isRejected}
                  aria-label={t("Award to {{supplier}}", { supplier: bid.supplierName || t("Unknown") }) ?? undefined}
                />
              ) : mode === "select" ? (
                <Checkbox
                  isChecked={isWinner}
                  onChange={() => onToggle(bid.bidId)}
                  colorScheme="teal"
                  isDisabled={isRejected}
                  minW={byJury ? "44px" : undefined}
                  minH={byJury ? "44px" : undefined}
                  justifyContent={byJury ? "center" : undefined}
                  aria-label={t("Award to {{supplier}}", { supplier: bid.supplierName || t("Unknown") }) ?? undefined}
                />
              ) : isWinner ? (
                <Box
                  as={FiAward}
                  color="green.500"
                  boxSize="5"
                  data-testid="award-winner-icon"
                  aria-label={t("Winner") ?? undefined}
                />
              ) : null}
            </Td>
          );
          return (
            <Tr
              key={bid.bidId}
              bg={isRejected ? "red.50" : isWinner ? "green.50" : undefined}
              onClick={rowToggles ? () => onToggle(bid.bidId) : undefined}
              cursor={rowToggles ? "pointer" : undefined}
            >
              {!byJury && selectCell}
              <Td w="1px" textAlign="center">
                <Flex
                  align="center"
                  justify="center"
                  display="inline-flex"
                  boxSize="7"
                  rounded="full"
                  bg={
                    isRejected
                      ? "red.100"
                      : isWinner
                        ? "smPrimary"
                        : "smBackgroundSecondary"
                  }
                  color={
                    isRejected ? "red.700" : isWinner ? "white" : "smMuted"
                  }
                  fontWeight="bold"
                  fontSize="sm"
                >
                  {isRejected || isUnranked ? "–" : bid.rank}
                </Flex>
              </Td>
              <Td w="100%" whiteSpace={byJury ? "normal" : undefined}>
                <Flex align="center" gap="2" wrap="wrap">
                  <Text fontWeight="semibold" color="smText">
                    {bid.supplierName || t("Unknown")}
                  </Text>
                  {isRejected && (
                    <Badge colorScheme="red">{t("Rejected")}</Badge>
                  )}
                  {isUnranked && <Badge colorScheme="gray">{t("Not ranked")}</Badge>}
                  <NotInRoundBadge notInRound={bid.notInRound} />
                  {!isRejected && (
                    <MinimumRequirementOutcomeBadge
                      minimumRequirements={bid.minimumRequirements}
                    />
                  )}
                </Flex>
                {!priceOnly && !byJury && (
                  <Text fontSize="sm" color="smMuted">
                    {formatPrice(bid.effectivePrice ?? bid.price)}
                  </Text>
                )}
              </Td>
              {byJury ? selectCell : priceOnly ? (
                <Td w="1px" isNumeric whiteSpace="nowrap" fontWeight="medium" color="smText">
                  {formatPrice(bid.effectivePrice ?? bid.price)}
                </Td>
              ) : (
                <Td
                  w="1px"
                  isNumeric
                  whiteSpace="nowrap"
                  fontWeight="bold"
                  fontFamily="mono"
                  color={isWinner ? "smPrimary" : "smText"}
                >
                  {hasAnyScore(bid) ? bid.weightedTotal.toFixed(1) : "–"}
                </Td>
              )}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );

  return (
    <Box
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      overflow="hidden"
    >
      <TableContainer>{table}</TableContainer>
    </Box>
  );
};
